/**
 * Provider framework config — mirrors shared/providerFrameworkConfig.js (AUTH0.IMPL.5.5 / IMPL.8).
 * Canonical source: shared/providerFrameworkConfig.js.
 * Alignment: shared/__tests__/providerFrameworkConfigAlignment.test.js
 *
 * @module client/utils/providerFrameworks
 */

import { getProviderMeta, resolveProviderId } from "./providerContext.js";

export const OKTA_BASELINE_ID = "OKTA-BASELINE";

export const STANDARD_FRAMEWORK_IDS = [
  "NIST-800-63B",
  "SOC2",
  "HIPAA",
  "PCI-DSS-v4",
];

/** @type {Record<string, { label: string, description: string, methodologyNote: string }>} */
const FRAMEWORK_COPY_BY_ID = {
  "NIST-800-63B": {
    label: "NIST 800-63B",
    description: "US federal digital identity authentication guidelines.",
    methodologyNote: "Maps password, MFA, and session findings to NIST 800-63B controls.",
  },
  SOC2: {
    label: "SOC 2",
    description: "AICPA Trust Services Criteria for access control and monitoring.",
    methodologyNote: "Prioritizes findings tagged with SOC 2 CC6.x controls.",
  },
  HIPAA: {
    label: "HIPAA",
    description: "HIPAA Security Rule technical safeguards (45 CFR §164.312).",
    methodologyNote: "Surfaces findings mapped to HIPAA access and authentication controls.",
  },
  "PCI-DSS-v4": {
    label: "PCI-DSS v4.0",
    description: "Payment card industry authentication and access requirements.",
    methodologyNote: "Highlights findings mapped to PCI DSS v4 authentication requirements.",
  },
  [OKTA_BASELINE_ID]: {
    label: "Okta Baseline",
    description: "Default Okta assessment scope (composes NIST 800-63B).",
    methodologyNote: "Use when no specific framework is selected; expands to NIST for scoring.",
  },
};

function frameworksCopyFor(supportedIds) {
  /** @type {Record<string, { label: string, description: string, methodologyNote: string }>} */
  const out = {};
  for (const id of supportedIds) {
    if (FRAMEWORK_COPY_BY_ID[id]) out[id] = FRAMEWORK_COPY_BY_ID[id];
  }
  return out;
}

export const OKTA_FRAMEWORK_CONFIG = {
  supportedFrameworks: [...STANDARD_FRAMEWORK_IDS, OKTA_BASELINE_ID],
  defaultFramework: OKTA_BASELINE_ID,
  baselineFrameworkId: OKTA_BASELINE_ID,
  frameworksCopy: frameworksCopyFor([...STANDARD_FRAMEWORK_IDS, OKTA_BASELINE_ID]),
  pickerHint: "If you select any specific framework, Okta Baseline auto-deselects.",
  emptySelectionHint: "Okta Baseline is the default when no other framework is selected.",
};

export const ENTRA_FRAMEWORK_CONFIG = {
  supportedFrameworks: [...STANDARD_FRAMEWORK_IDS],
  defaultFramework: "NIST-800-63B",
  baselineFrameworkId: null,
  frameworksCopy: frameworksCopyFor([...STANDARD_FRAMEWORK_IDS]),
  pickerHint: "NIST 800-63B is applied when no framework is selected.",
  emptySelectionHint: "NIST 800-63B is the default when no framework is selected.",
};

export const AUTH0_FRAMEWORK_CONFIG = {
  supportedFrameworks: [...STANDARD_FRAMEWORK_IDS],
  defaultFramework: "NIST-800-63B",
  baselineFrameworkId: null,
  frameworksCopy: frameworksCopyFor([...STANDARD_FRAMEWORK_IDS]),
  pickerHint: "NIST 800-63B is applied when no framework is selected.",
  emptySelectionHint: "NIST 800-63B is the default when no framework is selected.",
};

export const ORY_FRAMEWORK_CONFIG = {
  supportedFrameworks: [...STANDARD_FRAMEWORK_IDS],
  defaultFramework: "NIST-800-63B",
  baselineFrameworkId: null,
  frameworksCopy: frameworksCopyFor([...STANDARD_FRAMEWORK_IDS]),
  pickerHint: "NIST 800-63B is applied when no framework is selected.",
  emptySelectionHint: "NIST 800-63B is the default when no framework is selected.",
};

/**
 * @param {{ supportedFrameworks?: string[], defaultFramework?: string, baselineFrameworkId?: string | null }} fw
 * @param {string[] | null | undefined} list
 */
export function normalizeComplianceFrameworksForSubmit(fw, list) {
  const defaultFw = fw?.defaultFramework || "NIST-800-63B";
  const baselineId = fw?.baselineFrameworkId || null;
  const arr = Array.isArray(list)
    ? [...new Set(list.map((x) => String(x || "").trim()).filter(Boolean))]
    : [];

  if (!arr.length) return [defaultFw];

  if (baselineId) {
    const hasSpecific = arr.some((x) => x !== baselineId);
    if (hasSpecific) return arr.filter((x) => x !== baselineId);
    return [baselineId];
  }

  const filtered = arr.filter((x) => x !== OKTA_BASELINE_ID);
  return filtered.length ? filtered : [defaultFw];
}

/**
 * @param {string | null | undefined} providerId
 * @param {string[] | null | undefined} list
 */
export function normalizeProviderComplianceFrameworks(providerId, list) {
  return normalizeComplianceFrameworksForSubmit(getProviderMeta(providerId).frameworks, list);
}

/**
 * @param {string | null | undefined} providerId
 */
export function getDefaultProviderComplianceFrameworks(providerId) {
  const fw = getProviderMeta(providerId).frameworks;
  return [fw?.defaultFramework || "NIST-800-63B"];
}

/**
 * @param {string | null | undefined} providerId
 */
export function getProviderFrameworkOptions(providerId) {
  const meta = getProviderMeta(providerId);
  const fw = meta.frameworks;
  return (fw?.supportedFrameworks || STANDARD_FRAMEWORK_IDS).map((id) => ({
    id,
    label: fw?.frameworksCopy?.[id]?.label || id,
    description: fw?.frameworksCopy?.[id]?.description || "",
    methodologyNote: fw?.frameworksCopy?.[id]?.methodologyNote || "",
    isBaseline: id === fw?.baselineFrameworkId,
  }));
}

/**
 * @param {{ providerId?: string, provider_id?: string } | string | null | undefined} projectOrId
 */
export function resolveProjectProviderForFrameworks(projectOrId) {
  return resolveProviderId(
    typeof projectOrId === "string" ? { providerId: projectOrId } : projectOrId,
  );
}
